import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import { articleUrl, categoryUrl, formatDate, cx } from "../../lib/format";

const Img = ({ a, className, sizes = "(max-width: 768px) 100vw, 33vw", eager }) => (
  <img src={a.featured_image} alt={a.image_alt || a.title} loading={eager ? "eager" : "lazy"} decoding="async" sizes={sizes} className={cx("h-full w-full object-cover", className)} />
);

export const ArticleCard = ({ article: a, variant = "default", eager = false }) => {
  if (!a) return null;
  const meta = (
    <div className="flex items-center gap-3 text-xs text-ab-muted">
      <time dateTime={a.published_at}>{formatDate(a.published_at)}</time>
      <span aria-hidden="true">·</span>
      <span className="flex items-center gap-1"><Clock size={12} /> {a.reading_time} min read</span>
    </div>
  );

  if (variant === "compact") {
    return (
      <article className="group flex gap-4" data-testid={`article-card-${a.slug}`}>
        <Link to={articleUrl(a)} className="img-hover block h-20 w-24 shrink-0 overflow-hidden rounded-xl bg-ab-soft" tabIndex={-1} aria-hidden="true"><Img a={a} sizes="120px" /></Link>
        <div className="min-w-0">
          <Link to={categoryUrl(a.category_slug)} className="eyebrow">{a.category_name}</Link>
          <h3 className="mt-1 font-serif text-base font-semibold leading-snug"><Link to={articleUrl(a)} className="hover:text-ab-accent" data-testid={`article-card-link-${a.slug}`}>{a.title}</Link></h3>
          <div className="mt-1.5">{meta}</div>
        </div>
      </article>
    );
  }

  if (variant === "horizontal") {
    return (
      <article className="group grid gap-5 sm:grid-cols-5" data-testid={`article-card-${a.slug}`}>
        <Link to={articleUrl(a)} className="img-hover block aspect-[4/3] overflow-hidden rounded-2xl bg-ab-soft sm:col-span-2" tabIndex={-1} aria-hidden="true"><Img a={a} sizes="(max-width: 640px) 100vw, 40vw" /></Link>
        <div className="sm:col-span-3">
          <Link to={categoryUrl(a.category_slug)} className="eyebrow">{a.category_name}</Link>
          <h3 className="mt-2 font-serif text-xl font-semibold leading-snug sm:text-2xl"><Link to={articleUrl(a)} className="hover:text-ab-accent" data-testid={`article-card-link-${a.slug}`}>{a.title}</Link></h3>
          <p className="mt-2 line-clamp-2 text-sm text-ab-muted">{a.excerpt}</p>
          <div className="mt-3">{meta}</div>
        </div>
      </article>
    );
  }

  if (variant === "text") {
    return (
      <article className="group border-t border-ab-border py-5" data-testid={`article-card-${a.slug}`}>
        <h3 className="font-serif text-lg font-semibold leading-snug"><Link to={articleUrl(a)} className="hover:text-ab-accent" data-testid={`article-card-link-${a.slug}`}>{a.title}</Link></h3>
        <div className="mt-2">{meta}</div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col" data-testid={`article-card-${a.slug}`}>
      <Link to={articleUrl(a)} className="img-hover block aspect-[16/11] overflow-hidden rounded-2xl bg-ab-soft" tabIndex={-1} aria-hidden="true"><Img a={a} eager={eager} /></Link>
      <div className="flex flex-1 flex-col pt-5">
        <Link to={categoryUrl(a.category_slug)} className="eyebrow">{a.category_name}</Link>
        <h3 className="mt-2 font-serif text-xl font-semibold leading-snug"><Link to={articleUrl(a)} className="hover:text-ab-accent" data-testid={`article-card-link-${a.slug}`}>{a.title}</Link></h3>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-ab-muted">{a.excerpt}</p>
        <div className="mt-auto pt-4">{meta}</div>
      </div>
    </article>
  );
};

export const ArticleGrid = ({ articles = [], cols = 3, variant = "default", eagerFirst = 0 }) => (
  <div className={cx("grid gap-x-8 gap-y-12", cols === 2 ? "sm:grid-cols-2" : cols === 4 ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-2 lg:grid-cols-3")}>
    {articles.map((a, i) => <ArticleCard key={a.id} article={a} variant={variant} eager={i < eagerFirst} />)}
  </div>
);
